import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button, ButtonAdd } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
  cn,
  formatPrice,
  formatSideType,
  getImageUrl,
} from "@/lib/utils";
import { useModal, useRestaurantCurrency } from "@/lib/hooks";
import {
  MenuCombo,
  MenuComboComponent,
} from "@/redux/services/menu/menu_combo.type";
import {
  useDeleteMenuComboMutation,
  useDeleteMenuComboComponentMutation,
  useGetMenuComboQuery,
  useGetMenuComboComponentsQuery,
} from "@/redux/services/menu/menu_combo.api";
import MenuComboComponentModal from "./MenuComboComponentModal";
import { ChevronDown, Pencil, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

const Section = ({
  title,
  defaultOpen = true,
  children,
  rightSlot,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  rightSlot?: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section>
        <div className="flex items-center justify-between border-b border-border">
          <CollapsibleTrigger className="flex-1 flex items-center justify-between py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-card-foreground transition-colors">
            <span>{title}</span>
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                open && "rotate-180"
              )}
            />
          </CollapsibleTrigger>
          {rightSlot}
        </div>
        <CollapsibleContent className="pt-4 space-y-3">
          {children}
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
};

const MetaRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-card-foreground font-medium text-right">{value}</span>
  </div>
);

interface Props {
  comboId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (combo: MenuCombo) => void;
}

const MenuComboDetailsDrawer: React.FC<Props> = ({
  comboId,
  isOpen,
  onClose,
  onEdit,
}) => {
  const currency = useRestaurantCurrency();
  const confirm = useConfirm();

  const { data: combo, isFetching } = useGetMenuComboQuery(comboId ?? "", {
    skip: !comboId,
  });

  const { data: components, refetch: refetchComponents } =
    useGetMenuComboComponentsQuery(
      { menuComboId: comboId ?? "" },
      { skip: !comboId }
    );

  const [deleteCombo, { isLoading: isDeleting }] =
    useDeleteMenuComboMutation();

  const {
    isOpen: isComponentModalOpen,
    item: selectedComponent,
    openModal: addComponent,
    openEditModal: editComponent,
    closeModal: closeComponentModal,
  } = useModal<MenuComboComponent>();

  const handleDelete = async () => {
    if (!combo) return;
    const ok = await confirm({
      title: `Supprimer "${combo.name}" ?`,
      description: "Cette action est irréversible.",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    try {
      await deleteCombo(combo.id).unwrap();
      toast.success("Combo supprimé");
      onClose();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const sortedComponents = [...(components?.results ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder
  );

  return (
    <Sheet open={isOpen} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col gap-0"
      >
        <header className="flex-shrink-0 px-6 py-4 border-b border-border bg-card">
          <SheetTitle className="text-base">
            {combo ? combo.name : "Détails du combo"}
          </SheetTitle>
          <SheetDescription hidden>
            Détails et actions pour ce combo.
          </SheetDescription>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          {isFetching && !combo ? (
            <div className="space-y-3">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : combo ? (
            <>
              <Section title="Aperçu">
                {combo.image?.url && (
                  <div className="w-32 h-32 bg-muted/50 rounded-full overflow-hidden border-2 border-border">
                    <img
                      src={getImageUrl(combo.image.url)}
                      alt={combo.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <MetaRow
                    label="Prix"
                    value={
                      <span className="text-primary font-semibold">
                        {formatPrice(combo.price / 100, currency)}
                      </span>
                    }
                  />
                  <MetaRow label="Position" value={combo.sortOrder} />
                </div>
                {combo.description && (
                  <p className="text-sm text-muted-foreground bg-muted/50 rounded-md p-3">
                    {combo.description}
                  </p>
                )}
                <span
                  className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                    combo.isAvailable
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {combo.isAvailable ? "Disponible" : "Non disponible"}
                </span>
              </Section>

              <Section
                title={`Composants (${sortedComponents.length})`}
                rightSlot={
                  <ButtonAdd
                    label="Ajouter"
                    type="button"
                    onClick={addComponent}
                  />
                }
              >
                {sortedComponents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Aucun composant. Ajoutez-en un pour commencer.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {sortedComponents.map((component) => (
                      <ComponentCard
                        key={component.id}
                        component={component}
                        onEdit={() => editComponent(component)}
                        onAfterDelete={refetchComponents}
                      />
                    ))}
                  </div>
                )}
              </Section>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Impossible de charger les détails.
            </p>
          )}
        </div>

        <footer className="flex-shrink-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-2">
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={!combo || isDeleting}
          >
            <Trash2 className="h-4 w-4" />
            Supprimer
          </Button>
          <Button
            type="button"
            variant="accent"
            onClick={() => combo && onEdit(combo)}
            disabled={!combo}
          >
            <Pencil className="h-4 w-4" />
            Modifier
          </Button>
        </footer>
      </SheetContent>

      {isComponentModalOpen && combo && (
        <MenuComboComponentModal
          isOpen={isComponentModalOpen}
          onClose={closeComponentModal}
          component={selectedComponent}
          menuCombo={combo}
          onSuccess={refetchComponents}
        />
      )}
    </Sheet>
  );
};

interface ComponentCardProps {
  component: MenuComboComponent;
  onEdit: () => void;
  onAfterDelete: () => void;
}

const ComponentCard: React.FC<ComponentCardProps> = ({
  component,
  onEdit,
  onAfterDelete,
}) => {
  const [deleteComponent, { isLoading: isDeleting }] =
    useDeleteMenuComboComponentMutation();
  const confirm = useConfirm();

  const handleDelete = async () => {
    const ok = await confirm({
      title: `Supprimer "${component.name}" ?`,
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    try {
      await deleteComponent(component.id).unwrap();
      toast.success("Composant supprimé");
      onAfterDelete();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div className="border border-border rounded-md p-3 space-y-2 bg-muted/30">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-card-foreground">
            {component.name}
          </p>
          <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
            <span className="px-1.5 py-0.5 rounded bg-card text-muted-foreground border border-border">
              Qté {component.quantity}
            </span>
            <span className="px-1.5 py-0.5 rounded bg-card text-muted-foreground border border-border">
              Max {component.maxChoiceCount}
            </span>
            <span
              className={`px-1.5 py-0.5 rounded ${
                component.isRequired
                  ? "bg-blue-100 text-blue-800"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {component.isRequired ? "Requis" : "Optionnel"}
            </span>
            {component.isSubstitutable && (
              <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-800">
                Substituable
              </span>
            )}
          </div>
          {component.menuSection && (
            <p className="text-xs text-muted-foreground mt-1">
              Section : {component.menuSection.title}
            </p>
          )}
          {component.description && (
            <p className="text-xs text-muted-foreground mt-1">
              {component.description}
            </p>
          )}
          {component.includedMenuItems.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              <span className="font-medium">Inclus : </span>
              {component.includedMenuItems
                .map((item) => item.title)
                .join(", ")}
            </p>
          )}
          {component.excludedMenuItems.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              <span className="font-medium">Exclus : </span>
              {component.excludedMenuItems
                .map((item) => item.title)
                .join(", ")}
            </p>
          )}
          {component.allowedSideTypes.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              <span className="font-medium">Accompagnements : </span>
              {component.allowedSideTypes
                .map((type) => formatSideType(type))
                .join(", ")}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="p-1 rounded hover:bg-card text-muted-foreground hover:text-primary"
            title="Modifier"
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="p-1 rounded hover:bg-card text-muted-foreground hover:text-danger disabled:opacity-50"
            title="Supprimer"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MenuComboDetailsDrawer;
